import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ShieldCheck, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Section, Eyebrow, H1, Lead } from "../components/ui-kit";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRUST_POINTS = [
  "Walkthrough of the live Admin Switch, Lockout firewall, and Wellness telemetry",
  "Pricing context aligned to your lab size and student cohort",
  "Pilot-cohort eligibility check and 48-hour partnership response",
];

export default function PerforangeOS() {
  const [form, setForm] = useState({
    full_name: "",
    role: "",
    organization: "",
    email: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        lead_type: "school",
        full_name: form.full_name,
        role: form.role,
        organization: form.organization,
        email: form.email,
        message: `Perforange OS demo request from ${form.role || "an institutional lead"} at ${form.organization || "an unnamed institution"}.`,
      });
      toast.success("Request received. Our partnerships team will respond within 48 hours.");
      setSubmitted(true);
      setForm({ full_name: "", role: "", organization: "", email: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Submission failed. Please review the fields.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="perforange-os-gate">
      <Seo
        title="Perforange OS · Restricted Access"
        description="The Perforange OS suite is restricted to authorized partner institutions and verified pilot schools. Request a demo."
      />

      <Section className="!pt-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="absolute inset-0 bg-radial-orange pointer-events-none" />

        <div className="relative grid lg:grid-cols-12 gap-10 items-start">
          {/* LEFT — Lockout panel */}
          <div className="lg:col-span-6">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF8C00]/40 bg-[#FF8C00]/8"
              data-testid="restricted-badge"
            >
              <Lock className="w-3.5 h-3.5 text-[#FF8C00]" />
              <span className="text-[10px] font-mono-tech uppercase tracking-[0.28em] text-[#FF8C00] font-black">
                Restricted Access
              </span>
            </div>
            <Eyebrow className="mt-6">Perforange OS</Eyebrow>
            <H1 className="!leading-[1.05]">
              The OS is <span className="text-gradient-orange">locked</span> by design.
            </H1>
            <Lead className="mt-6 max-w-xl">
              Access to the Perforange OS suite is restricted to authorized partner institutions and verified pilot schools.
            </Lead>

            <div className="mt-10 rounded-2xl border border-white/8 bg-[#0C0C10]/70 p-6 backdrop-blur-sm" data-testid="trust-callout">
              <p className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#00FFD0] mb-4">
                What a demo includes
              </p>
              <ul className="space-y-3">
                {TRUST_POINTS.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#D4D4D8] leading-relaxed">
                    <CheckCircle2 className="w-5 h-5 text-[#00FFD0] shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="mt-8 text-xs text-[#6a6a70] flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-[#FF8C00]" />
              Submissions are reviewed by the Perforange partnerships team. Verified-domain institutional emails accelerate access.
            </p>
          </div>

          {/* RIGHT — Form */}
          <div className="lg:col-span-6">
            {submitted ? (
              <div
                data-testid="os-gate-success"
                className="relative rounded-2xl border border-[#00FFD0]/30 bg-gradient-to-br from-[#082019] via-[#0A0A0C] to-[#0C0C10] p-8 md:p-12 overflow-hidden"
              >
                <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#00FFD0]/15 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-[#00FFD0]/10 border border-[#00FFD0]/40 flex items-center justify-center mb-5">
                    <CheckCircle2 className="w-6 h-6 text-[#00FFD0]" />
                  </div>
                  <p className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#00FFD0]">Signal Received</p>
                  <h2 className="mt-2 font-display font-black text-2xl md:text-3xl text-white leading-tight">
                    Your request is in the queue.
                  </h2>
                  <p className="mt-4 text-sm text-[#D4D4D8] leading-relaxed max-w-md">
                    A partnerships lead will respond within 48 hours, typically faster for verified institutional emails. You&apos;ll receive a confirmation email shortly with our 1-page Executive Brief attached.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    data-testid="os-gate-submit-another"
                    className="mt-6 text-xs font-mono-tech uppercase tracking-widest text-[#FF8C00] hover:underline"
                  >
                    Submit another request →
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="os-gate-form"
                className="relative rounded-2xl border border-white/8 bg-[#0C0C10] p-6 md:p-8 space-y-5 overflow-hidden"
              >
                <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FF8C00]/10 blur-3xl pointer-events-none" />
                <div className="relative space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-mono-tech uppercase tracking-widest text-[#FF8C00]">Request a Demo</p>
                      <h2 className="mt-1 font-display font-black text-xl text-white">Verify your institution</h2>
                    </div>
                    <span className="text-[10px] font-mono-tech text-[#6a6a70] uppercase tracking-widest">Step 1 / 1</span>
                  </div>

                  <Field
                    label="Full Name *"
                    name="full_name"
                    value={form.full_name}
                    onChange={onChange}
                    required
                    placeholder="e.g., Anjali Mehta"
                  />
                  <Field
                    label="Designation *"
                    name="role"
                    value={form.role}
                    onChange={onChange}
                    required
                    placeholder="e.g., Principal / Trustee / Director"
                  />
                  <Field
                    label="School / Institution Name *"
                    name="organization"
                    value={form.organization}
                    onChange={onChange}
                    required
                    placeholder="e.g., DPS Bangalore South"
                  />
                  <Field
                    label="Official Email ID *"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    placeholder="name@institution.edu.in"
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="os-gate-submit"
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#FF8C00] text-[#0A0A0C] font-black text-base uppercase tracking-widest hover:bg-[#FFA333] hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] disabled:opacity-60 transition-all"
                  >
                    {loading ? "Submitting…" : "Request a Demo"} <ArrowUpRight className="w-5 h-5" />
                  </button>

                  <p className="text-[11px] text-[#6a6a70] leading-relaxed">
                    By submitting, you agree to be contacted by Perforange&apos;s partnerships team regarding access to the Perforange OS suite.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required, placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={`input-${name}`}
        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[#6a6a70] focus:outline-none focus:border-[#FF8C00] text-sm"
      />
    </div>
  );
}
